import { Admin, CustomRoutes, Resource } from 'react-admin';
import polyglotI18nProvider from 'ra-i18n-polyglot';
import { Route } from 'react-router';
import './styles.css';
import authProvider from './authProvider';
import { Login, Layout } from './layout';
import englishMessages from './i18n/en';
import { lightTheme } from './layout/themes';
import Configuration from './configuration/Configuration';
import organizations from './resources/organizations';
import users from './resources/users';
import banners from './resources/banners';
import programs from './resources/programs';
import categories from './resources/categories';
import SubjectCreate from './resources/subjects/SubjectCreate';
import SubjectList from './resources/subjects/SubjectList';
import SubjectEdit from './resources/subjects/SubjectEdit';
import ChapterList from './resources/chapters/ChapterList';
import ChapterCreate from './resources/chapters/ChapterCreate';
import ChapterEdit from './resources/chapters/ChapterEdit';
import TopicList from './resources/topics/TopicList';
import TopicCreate from './resources/topics/TopicCreate';
import TopicEdit from './resources/topics/TopicEdit';
import ContentList from './resources/contents/ContentList';
import ContentCreate from './resources/contents/ContentCreate';
import ContentEdit from './resources/contents/ContentEdit';
import batch_templates from './resources/batch_templates';
import batches from './resources/batches';
import issue_templates from './resources/issue_templates';
import issue_topics from './resources/issue_topics';
import blogs from './resources/blogs';
import coupons from './resources/coupons';
import { Dashboard } from './resources/dashboard';
import ProductTemplateList from './resources/product_templates/ProductTemplateList';
import ProductTemplatesCreate from './resources/product_templates/ProductTemplatesCreate';
import ProductTemplateEdit from './resources/product_templates/ProductTemplateEdit';
import ProductList from './resources/products/ProductList';
import ProductCreate from './resources/products/ProductCreate';
import ProductEdit from './resources/products/ProductEdit';
import AttributeValueList from './resources/attribute_values/AttributeValueList';
import AttributeValueCreate from './resources/attribute_values/AttributeValueCreate';
import AttributeValueEdit from './resources/attribute_values/AttributeValueEdit';
import AttributeList from './resources/attributes/AttributeList';
import AttributeCreate from './resources/attributes/AttributeCreate';
import AttributeEdit from './resources/attributes/AttributeEdit';
import TestList from './resources/tests/TestList';
import TestCreate from './resources/tests/TestCreate';
import TestEdit from './resources/tests/TestEdit';
import SectionList from './resources/sections/SectionList';
import SectionCreate from './resources/sections/SectionCreate';
import SectionEdit from './resources/sections/SectionEdit';
import QuestionCreate from './resources/questions/QuestionCreate';
import QuestionEdit from './resources/questions/QuestionEdit';
import otps from './resources/otps';
import app_versions from './resources/app_versions';
import testimonials from './resources/testimonials';
import faqs from './resources/faqs';
import doubts from './resources/doubts';
import blog_topics from './resources/blog_topics';
import StudentResponseList from './resources/student_responses/StudentResponseList';
import TestShow from './resources/tests/TestShow';
import organization_resources from './resources/organization_resources';
import video_subjects from './resources/video_subjects';
import videos from './resources/videos';
import batch_schedules from './resources/batch_schedules';
import download_categories from './resources/download_categories';
import downloads from './resources/downloads';
import podcast_topics from './resources/podcast_topics';
import podcasts from './resources/podcasts';
import orders from './resources/orders';
import Store from './resources/store/Store';
import student_video_stats from './resources/student_video_stats';
import ReportUserSignupsList from './resources/reports/report_user_signups/ReportUserSignupsList';
import ReportTestsList from './resources/reports/report_tests/ReportTestsList';
import ReportStoreItemsList from './resources/reports/report_store_items/ReportStoreItemsList';
import ReportOrdersList from './resources/reports/report_orders/ReportOrdersList';
import ReportBatchStudentList from './resources/reports/report_batch_students/ReportBatchStudentsList';
import ReportApplicationUsersList from './resources/reports/report_application_users/ReportApplicationUsersList';
import SubjectShow from './resources/subjects/SubjectShow';
import ChapterShow from './resources/chapters/ChapterShow';
import queries from './resources/queries';
import non_category_products from './resources/non_category_products';
import NonCategoryProductCreate from './resources/non_category_products/NonCategoryProductCreate';
import NonCategoryProductEdit from './resources/non_category_products/NonCategoryProductEdit';
import demo_videos_categories from './resources/demo_videos_categories';
import issues from './resources/issues';
import deleted_users from './resources/deleted_users';
import BatchSchedulePreview from './resources/batch_schedules/BatchSchedulePreview';
import segmentations from './resources/segmentations';
import campaigns from './resources/campaigns';
import CommentsList from './resources/comments/CommentsList';
import ProductShow from './resources/products/ProductShow';
import QuestionBankList from './resources/questions_bank/QuestionBankList';
import QuestionBankCreate from './resources/questions_bank/QuestionBankCreate';
import QuestionBankEdit from './resources/questions_bank/QuestionBankEdit';
import pdf_categories from './resources/pdf_categories';
import pdfs from './resources/pdfs';
import roles from './resources/roles';
import { restDataProvider } from './dataProviders/restDataProvider/restDataProvider';
import permissions from './resources/permissions';
import MetaDataList from './resources/metadata/MetaDataList';
import test_serieses from './resources/test_serieses';
import ExercisesList from './resources/exercise/ExerciseList';
import ExerciseQuestionEdit from './resources/exercise/ExerciseQuestionEdit';
import activation_codes from './resources/activation_codes';
import ActivationCodesList from './resources/activation_codes/ActivationCodesLists';
import SeperateChatPreview from './resources/batch_schedules/SeparateChat';
import banner_categories from './resources/banner_categories';
import testimonial_categories from './resources/testimonial_categories';
import faq_categories from './resources/faq_categories';
import live_channels from './resources/live_channels';
import BulkUploadList from './resources/bulk-uploads/BulkUploadList';
import BulkUploadCreate from './resources/bulk-uploads/BulkUploadCreate';
import demo_videos from './resources/demo_videos';
import metadataResources from './resources/metadata';
import QuestionPaperView from './common/question-paper/QuestionPaperView';
import ExerciseEdit from './resources/exercise/ExerciseEdit';
import ExerciseCreate from './resources/exercise/ExerciseCreate';
import CronJobsList from './resources/cron_jobs/CronJobsList';
import cronJobsResources from './resources/cron_jobs';
import CronJobsEdit from './resources/cron_jobs/CronJobsEdit';
import QuestionListNew from './resources/questions/QuestionListNew';


const i18nProvider = polyglotI18nProvider(locale => {
    if (locale === 'fr') {
        return import('./i18n/fr').then(messages => messages.default);
    }
    if (locale === 'hi') {
        return import('./i18n/hi').then(messages => messages.default);
    }

    // Always fallback on english
    return englishMessages;
}, 'en');

const App = () => {
    const dataProvider = restDataProvider;

    return (
        <Admin
            title=""
            dataProvider={dataProvider}
            authProvider={authProvider}
            loginPage={Login}
            layout={Layout}
            dashboard={Dashboard}
            i18nProvider={i18nProvider}
            disableTelemetry
            theme={lightTheme}
        >
            <CustomRoutes>
                <Route path="/store" element={<Store />} />
                <Route path="/configuration" element={<Configuration />} />
                {/*Content Routes*/}
                <Route path="/programs/:programId/subjects" element={<SubjectList />} />
                <Route path="/programs/:programId/subjects/create" element={<SubjectCreate />} />
                <Route
                    path="/programs/:programId/subjects/:subjectId/edit"
                    element={<SubjectEdit />}
                />
                <Route
                    path="/programs/:programId/subjects/:subjectId/show"
                    element={<SubjectShow />}
                />

                <Route path="/test-series/:testSeriesId/tests" element={<TestList />} />
                <Route path="/test-series/:testSeriesId/tests/create" element={<TestCreate />} />
                <Route
                    path="/test-series/:testSeriesId/tests/:testId/edit"
                    element={<TestEdit />}
                />
                <Route
                    path="/test-series/:testSeriesId/tests/:testId/question_paper"
                    element={<QuestionPaperView />}
                />
                <Route
                    path="/programs/:programId/tests/:exerciseId/questions_list"
                    element={<QuestionListNew />}
                />

                {/* ----------------------------- exercise -------------- */}
                <Route path="/exercises" element={<ExercisesList />} />
                <Route path="/exercises/:testId/edit" element={<ExerciseEdit />} />
                <Route path="/exercises/create" element={<ExerciseCreate />} />
                <Route path="/exercises/:exerciseId/questions_list" element={<QuestionListNew />} />
                <Route
                    path="/exercises/:exerciseId/questions/:questionId/edit"
                    element={<ExerciseQuestionEdit />}
                />
                <Route path="exercise/:testId/question_paper" element={<TestShow />} />

                <Route
                    path="/programs/:programId/exercise/:testSeriesId/tests/:testId/question_paper"
                    element={<TestShow />}
                />

                {/* Batches */}
                <Route
                    path="/test-series/:testSeriesId/tests/:testId/sections"
                    element={<SectionList />}
                />

                <Route path="/tests/:testId/sections" element={<SectionList />} />

                <Route
                    path="/test-series/:testSeriesId/tests/:testId/sections/create"
                    element={<SectionCreate />}
                />

                <Route
                    path="/test-series/:testSeriesId/tests/:testId/sections/:sectionId/edit"
                    element={<SectionEdit />}
                />

                <Route
                    path="/test-series/:testSeriesId/tests/:testId/student_responses"
                    element={<StudentResponseList />}
                />

                <Route
                    path="/test-series/:testSeriesId/tests/:testId/sections/:sectionId/questions"
                    element={<QuestionListNew />}
                />

                <Route
                    path="/test-series/:testSeriesId/tests/:testId/sections/:sectionId/questions/create"
                    element={<QuestionCreate />}
                />
                <Route
                    path="/test-series/:testSeriesId/tests/:testId/sections/:sectionId/questions/:questionId/edit"
                    element={<QuestionEdit />}
                />

                <Route
                    path="/programs/:programId/subjects/:subjectId/chapters"
                    element={<ChapterList />}
                />
                <Route
                    path="/programs/:programId/subjects/:subjectId/chapters/create"
                    element={<ChapterCreate />}
                />
                <Route
                    path="/programs/:programId/subjects/:subjectId/chapters/:chapterId/edit"
                    element={<ChapterEdit />}
                />
                <Route
                    path="/programs/:programId/subjects/:subjectId/chapters/:chapterId/show"
                    element={<ChapterShow />}
                />

                <Route
                    path="/programs/:programId/subjects/:subjectId/chapters/:chapterId/topics"
                    element={<TopicList />}
                />
                <Route
                    path="/programs/:programId/subjects/:subjectId/chapters/:chapterId/topics/create"
                    element={<TopicCreate />}
                />
                <Route
                    path="/programs/:programId/subjects/:subjectId/chapters/:chapterId/topics/:topicId/edit"
                    element={<TopicEdit />}
                />
                <Route
                    path="/programs/:programId/subjects/:subjectId/chapters/:chapterId/topics/:topicId/contents"
                    element={<ContentList />}
                />
                <Route
                    path="/programs/:programId/subjects/:subjectId/chapters/:chapterId/topics/:topicId/contents/create"
                    element={<ContentCreate />}
                />
                <Route
                    path="/programs/:programId/subjects/:subjectId/chapters/:chapterId/topics/:topicId/contents/:contentId/edit"
                    element={<ContentEdit />}
                />

                {/*Ecom Routes*/}
                <Route
                    path="/ecom_categories/:categoryId/ecom_product_templates"
                    element={<ProductTemplateList />}
                />
                <Route
                    path="/ecom_categories/:categoryId/ecom_product_templates/create"
                    element={<ProductTemplatesCreate />}
                />
                <Route
                    path="/ecom_categories/:categoryId/ecom_product_templates/:productTemplateId/edit"
                    element={<ProductTemplateEdit />}
                />
                {/*Products*/}
                <Route
                    path="/ecom_categories/:categoryId/ecom_product_templates/:productTemplateId/ecom_products"
                    element={<ProductList />}
                />
                <Route
                    path="/ecom_categories/:categoryId/ecom_product_templates/:productTemplateId/ecom_products/create"
                    element={<ProductCreate />}
                />
                <Route
                    path="/ecom_categories/:categoryId/ecom_product_templates/:productTemplateId/ecom_products/:productId/edit"
                    element={<ProductEdit />}
                />
                <Route
                    path="/ecom_categories/:categoryId/ecom_product_templates/:productTemplateId/ecom_products/:productId/show"
                    element={<ProductShow />}
                />
                {/*Attributes*/}
                <Route
                    path="/ecom_categories/:categoryId/ecom_attributes"
                    element={<AttributeList />}
                />
                <Route
                    path="/ecom_categories/:categoryId/ecom_attributes/create"
                    element={<AttributeCreate />}
                />
                <Route
                    path="/ecom_categories/:categoryId/ecom_attributes/:attributeId/edit"
                    element={<AttributeEdit />}
                />
                {/*Attribute Values*/}
                <Route
                    path="/ecom_categories/:categoryId/ecom_attributes/:attributeId/ecom_attribute_values"
                    element={<AttributeValueList />}
                />
                <Route
                    path="/ecom_categories/:categoryId/ecom_attributes/:attributeId/ecom_attribute_values/create"
                    element={<AttributeValueCreate />}
                />
                <Route
                    path="/ecom_categories/:categoryId/ecom_attributes/:attributeId/ecom_attribute_values/:attributeValueId/edit"
                    element={<AttributeValueEdit />}
                />

                <Route path="/report/signups" element={<ReportUserSignupsList />} />
                <Route path="/report/tests" element={<ReportTestsList />} />
                <Route path="/report/store_items" element={<ReportStoreItemsList />} />
                <Route path="/report/orders" element={<ReportOrdersList />} />
                <Route path="/report/batch_students" element={<ReportBatchStudentList />} />
                <Route path="/report/application_users" element={<ReportApplicationUsersList />} />
                <Route
                    path="/ecom_product_templates/:productTemplateId/ecom_products/create"
                    element={<NonCategoryProductCreate />}
                />
                <Route
                    path="/ecom_product_templates/:productTemplateId/ecom_products/:productId/edit"
                    element={<NonCategoryProductEdit />}
                />

                <Route
                    path="/batch_schedules/:scheduleId/preview"
                    element={<BatchSchedulePreview />}
                />
                <Route path="/batch_schedules/:scheduleId/chat" element={<SeperateChatPreview />} />

                <Route path="/comments/:parentId/:parentType" element={<CommentsList />} />

                {/* ---------------------- questions bank------------------- */}
                <Route path="/questions_bank" element={<QuestionBankList />} />
                <Route path="/questions_bank/create" element={<QuestionBankCreate />} />
                <Route path="/questions_bank/:questionId/edit" element={<QuestionBankEdit />} />
                <Route path="/metadata" element={<MetaDataList />} />

                <Route path="/pendrive/activation-codes" element={<ActivationCodesList />} />

                <Route path="/bulk-uploads" element={<BulkUploadList />} />
                <Route path="/bulk-uploads/create" element={<BulkUploadCreate />} />

                {/* ---------------------- Cron jobs ------------------- */}
                <Route path="/cron-jobs" element={<CronJobsList />} />
                <Route path="/cron-jobs/:id" element={<CronJobsEdit />} />

            </CustomRoutes>
            <Resource name="test-series" {...test_serieses} />
            <Resource name="ecom_products" {...non_category_products} />
            <Resource name="segmentations" {...segmentations} />
            <Resource name="campaigns" {...campaigns} />
            <Resource name="deleted_users" {...deleted_users} />
            <Resource name="organizations" {...organizations} />
            <Resource name="ecom-orders" {...orders} />
            <Resource name="podcast-topics" {...podcast_topics} />
            <Resource name="podcasts" {...podcasts} />
            <Resource name="downloads" {...downloads} />
            <Resource name="download_categories" {...download_categories} />
            <Resource name="student_video_stats" {...student_video_stats} />
            <Resource name="batch_schedules" {...batch_schedules} />
            <Resource name="organization-resources" {...organization_resources} />
            <Resource name="ce_blog_topics" {...blog_topics} />
            <Resource name="faqs" {...faqs} />
            <Resource name="faq-categories" {...faq_categories} />
            <Resource name="doubts" {...doubts} />
            <Resource name="queries" {...queries} />
            <Resource name="testimonial-category" {...testimonial_categories} />
            <Resource name="testimonials" {...testimonials} />
            <Resource name="otps" {...otps} />
            <Resource name="app_versions" {...app_versions} />
            <Resource name="batch-templates" {...batch_templates} />
            <Resource name="batches" {...batches} />
            <Resource name="programs" {...programs} />
            <Resource name="users" {...users} />
            <Resource name="banners" {...banners} />
            <Resource name="banner-categories" {...banner_categories} />
            <Resource name="ecom-coupons" {...coupons} />
            <Resource name="ecom_categories" {...categories} />
            <Resource name="cs_issue_templates" {...issue_templates} />
            <Resource name="issue-topics" {...issue_topics} />
            <Resource name="issues" {...issues} />
            <Resource name="ce_blogs" {...blogs} />
            <Resource name="video-subjects" {...video_subjects} />
            <Resource name="videos" {...videos} />
            <Resource name="pdf-categories" {...pdf_categories} />
            <Resource name="pdfs" {...pdfs} />
            <Resource name="roles" {...roles} />
            <Resource name="permissions" {...permissions} />
            <Resource name="demo-video-categories" {...demo_videos_categories} />
            <Resource name="demo-videos" {...demo_videos} />
            <Resource name="activation-codes" {...activation_codes} />
            <Resource name="live_channels" {...live_channels} />
            <Resource name="master-classes" {...metadataResources} />
            <Resource name="master-subjects" {...metadataResources} />
            <Resource name="master-chapters" {...metadataResources} />
            <Resource name="master-topics" {...metadataResources} />
            <Resource name="cron_jobs" {...cronJobsResources} />
        </Admin>
    );
};

export default App;
